import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Loader2, Lock } from 'lucide-react'
import { cn } from '../../utils/cn'
import { ModernButton } from '../ui'
import type { SaleOnboardingProgress } from '../../types'

export type SaleOnboardingStepKey =
 | 'payment'
 | 'documentation'
 | 'commissions'
 | 'invoicing'
 | 'pipeline'

export const SALE_ONBOARDING_STEP_ORDER: SaleOnboardingStepKey[] = [
 'payment',
 'documentation',
 'commissions',
 'invoicing',
 'pipeline',
]

export const TAB_BY_ONBOARDING_STEP: Record<SaleOnboardingStepKey, string> = {
 payment: 'payments',
 documentation: 'documents',
 commissions: 'commissions',
 invoicing: 'financial',
 pipeline: 'administrative',
}

const SALE_DETAILS_TAB_TO_STEP: Record<string, SaleOnboardingStepKey> = {
 payments: 'payment',
 documents: 'documentation',
 commissions: 'commissions',
 financial: 'invoicing',
 administrative: 'pipeline',
}

export function stepCompleteForOnboarding(
 progress: SaleOnboardingProgress,
 key: SaleOnboardingStepKey
): boolean {
 switch (key) {
 case 'payment':
 return progress.payment.complete
 case 'documentation':
 return progress.documentation.complete
 case 'commissions':
 return progress.commissions.complete
 case 'invoicing':
 return progress.invoicing.complete
 case 'pipeline':
 return progress.preparation.complete
 default:
 return false
 }
}

export function isOnboardingStepTabLocked(
 tab: string,
 progress: SaleOnboardingProgress | null
): boolean {
 if (!progress) return false
 const stepKey = SALE_DETAILS_TAB_TO_STEP[tab]
 if (!stepKey) return false
 const idx = SALE_ONBOARDING_STEP_ORDER.indexOf(stepKey)
 if (idx <= 0) return false
 return SALE_ONBOARDING_STEP_ORDER.slice(0, idx).some(
 (k) => !stepCompleteForOnboarding(progress, k)
 )
}

export function getSuggestedSaleDetailsTabForOnboarding(
 progress: SaleOnboardingProgress
): string {
 for (const key of SALE_ONBOARDING_STEP_ORDER) {
 if (!stepCompleteForOnboarding(progress, key)) {
 return TAB_BY_ONBOARDING_STEP[key]
 }
 }
 return TAB_BY_ONBOARDING_STEP.pipeline
}

interface SaleOnboardingStepperProps {
 progress: SaleOnboardingProgress
 loading: boolean
 onGoToTab: (tab: string) => void
 onSendToPipeline: () => void
 sending: boolean
 canMutate: boolean
 operationalStage: string
 saleCancelled: boolean
 wizardMode?: boolean
 activeWizardStepIndex?: number
}

export const SaleOnboardingStepper: React.FC<SaleOnboardingStepperProps> = ({
 progress,
 loading,
 onGoToTab,
 onSendToPipeline,
 sending,
 canMutate,
 operationalStage,
 saleCancelled,
 wizardMode = false,
 activeWizardStepIndex = 0,
}) => {
 const { t } = useTranslation()

 if (saleCancelled) {
 return null
 }

 const steps: { key: SaleOnboardingStepKey; label: string; complete: boolean }[] =
 SALE_ONBOARDING_STEP_ORDER.map((key) => ({
 key,
 label: t(`financialSales.saleOnboarding.steps.${key}`),
 complete: stepCompleteForOnboarding(progress, key),
 }))

 const firstIncompleteIdx = steps.findIndex((s) => !s.complete)

 return (
 <div className="rounded-xl border border-border bg-card/50 p-4 space-y-4">
 <div className="flex flex-col gap-1">
 <h3 className="text-sm font-semibold text-foreground">{t('financialSales.saleOnboarding.title')}</h3>
 <p className="text-xs text-muted-foreground">{t('financialSales.saleOnboarding.subtitle')}</p>
 {loading && (
 <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
 <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
 {t('financialSales.saleOnboarding.syncing')}
 </div>
 )}
 </div>

 <div className="relative flex flex-wrap items-start justify-between gap-2">
 <div className="absolute left-0 right-0 top-5 h-px bg-border hidden md:block" aria-hidden />
 {steps.map((step, idx) => {
 const done = step.complete
 const locked = wizardMode
 ? idx > activeWizardStepIndex
 : idx > 0 && steps.slice(0, idx).some((prev) => !prev.complete)
 const isCurrent = wizardMode
 ? idx === activeWizardStepIndex
 : !done && idx === firstIncompleteIdx && !locked
 const clickable = !wizardMode && !locked && (done || isCurrent)

 return (
 <button
 key={step.key}
 type="button"
 disabled={!clickable}
 title={
 locked && !done ? t('financialSales.saleOnboarding.lockedStepHint') : undefined
 }
 aria-label={
 locked && !done
 ? `${step.label} — ${t('financialSales.saleOnboarding.lockedStepHint')}`
 : step.label
 }
 onClick={() => {
 if (clickable) onGoToTab(TAB_BY_ONBOARDING_STEP[step.key])
 }}
 className={cn(
 'relative flex flex-col items-center gap-2 z-10 flex-1 min-w-[72px] max-w-[120px]',
 'rounded-lg p-1 transition-opacity',
 clickable
 ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent cursor-pointer'
 : cn(
 'cursor-not-allowed',
 wizardMode && idx <= activeWizardStepIndex ? 'opacity-100' : 'opacity-50',
 ),
 )}
 >
 <div
 className={cn(
 'w-10 h-10 rounded-full flex items-center justify-center border-2 text-sm font-semibold transition-colors',
 done && 'bg-emerald-500 border-emerald-500 text-white',
 isCurrent && 'border-accent text-accent bg-background ring-4 ring-accent/15',
 !done &&
 !isCurrent &&
 locked &&
 'border-muted-foreground/40 text-muted-foreground bg-muted/30',
 !done && !isCurrent && !locked && 'border-border text-muted-foreground bg-background',
 )}
 >
 {done ? (
 <Check className="h-4 w-4" />
 ) : locked ? (
 <Lock className="h-4 w-4" />
 ) : (
 idx + 1
 )}
 </div>
 <span
 className={cn(
 'text-[0.65rem] font-semibold uppercase tracking-wide text-center leading-tight',
 isCurrent && 'text-accent',
 !isCurrent && done && 'text-emerald-600 dark:text-emerald-400',
 !isCurrent && !done && 'text-muted-foreground',
 )}
 >
 {step.label}
 </span>
 </button>
 )
 })}
 </div>

 {operationalStage === 'pre_sale' && !wizardMode && (
 <div className="flex flex-col gap-2 pt-2 border-t border-border">
 <div className="flex flex-col sm:flex-row sm:items-center gap-3">
 <ModernButton
 type="button"
 disabled={!progress.canSend || sending || !canMutate}
 onClick={() => void onSendToPipeline()}
 className="w-full sm:w-auto"
 >
 {sending ? (
 <>
 <Loader2 className="h-4 w-4 mr-2 animate-spin" />
 {t('financialSales.saleOnboarding.sending')}
 </>
 ) : (
 t('financialSales.saleOnboarding.sendCta')
 )}
 </ModernButton>
 {!progress.canSend && (
 <p className="text-xs text-muted-foreground">{t('financialSales.saleOnboarding.hintCompleteRequirements')}</p>
 )}
 </div>
 </div>
 )}

 {progress.preparation.complete && (
 <p className="text-xs text-emerald-600 dark:text-emerald-400">{t('financialSales.saleOnboarding.inPipeline')}</p>
 )}
 </div>
 )
}
